import { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { postApi } from "../../api/getUser";
import { fetchPartCategories } from "../../features/parts/partsSlice";

export const usePartCategory = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.user);
  const allPartCategories = useSelector((state) => state.parts.allPartCategories);

  // State
  const [showPartCategoryPopup, setShowPartCategoryPopup] = useState(false);
  const [tempPartCategoryData, setTempPartCategoryData] = useState(null);
  const [partCategoryPopupErr, setPartCategoryPopupErr] = useState("");
  const [isPartCategorySaving, setIsPartCategorySaving] = useState(false);
  const [isPartCategoryDeleting, setIsPartCategoryDeleting] = useState(false);

  // Handler for creating new Part Category
  const handleCreatePartCategory = (data) => {
    const tempData = {
      label: data,
      value: `${userId}_${Date.now()}`, // Generate temporary ID for new categories
      __isNew__: true,
    };

    setTempPartCategoryData(tempData);
    setShowPartCategoryPopup(true);
  };

  // Handler for saving part category from popup
  const handleSavePartCategory = async ({
    categoryName,
    categoryId,
    isNew,
    setValue,
    watch,
  }) => {
    // Duplicate Part Category Name check
    const existingPartCategories = allPartCategories.filter(
      (c) =>
        c.label?.toLowerCase() === categoryName?.toLowerCase() &&
        c.value !== categoryId
    );
    if (existingPartCategories.length > 0) {
      setPartCategoryPopupErr(t("dup_part_category_err_msg"));
      return;
    }
    
    setIsPartCategorySaving(true);
    setPartCategoryPopupErr("");

    try {
      const createApiJson = {
        querystring: "add_edit_part_category",
        category_name: categoryName,
        action: isNew ? 1 : 2,
        category_id: categoryId,
      };
      const response = await postApi(createApiJson, "inventory");

      if (!response?.success) {
        let errMsg = t("form_err_msg");
        if (response?.action_value === "record_exists") {
          errMsg = t("dup_part_category_err_msg");
        }
        setPartCategoryPopupErr(errMsg);
        return;
      }

      if (response?.success) {
        if (isNew) {
          // Set as selected value
          setValue("partCategory", { label: categoryName, value: categoryId });
        } else {
          // Update the form value if it's currently selected
          const currentValue = watch("partCategory");
          if (currentValue?.value === tempPartCategoryData.value) {
            setValue("partCategory", {
              label: categoryName,
              value: categoryId,
            });
          }
        }

        // Refresh part categories from backend
        dispatch(fetchPartCategories({ force: true }));

        // Close popup on success
        setShowPartCategoryPopup(false);
      }
    } catch (error) {
      console.error("Error saving part category:", error);
      setPartCategoryPopupErr(t("form_err_msg"));
    } finally {
      setIsPartCategorySaving(false);
    }
  };

  // Handler for deleting part category from popup
  const handleDeletePartCategory = async (categoryId, setValue, watch) => {
    setIsPartCategoryDeleting(true);
    setPartCategoryPopupErr("");

    try {
      const deleteApiData = {
        delete_ids: categoryId,
        querystring: "multiple_delete",
        sub_query_string: "PARTS_CATEGORY",
      };

      const response = await postApi(deleteApiData, "deleteData");

      if (!response?.success) {
        let errMsg = t("form_err_msg");
        if (response?.delete_result?.length > 0) {
          errMsg = t("delete_part_category_err");
        }
        setPartCategoryPopupErr(errMsg);
        return;
      }

      if (response?.success) {
        // Clear form value if it's currently selected
        const currentValue = watch("partCategory");
        if (currentValue?.value === tempPartCategoryData.value) {
          setValue("partCategory", null);
        }

        // Refresh part categories from backend
        dispatch(fetchPartCategories({ force: true }));

        // Close popup on success
        setShowPartCategoryPopup(false);
      }
    } catch (error) {
      console.error("Error deleting part category:", error);
      setPartCategoryPopupErr(t("form_err_msg"));
    } finally {
      setIsPartCategoryDeleting(false);
    }
  };

  // Handler for closing popup and clearing errors
  const handleClosePartCategoryPopup = () => {
    setPartCategoryPopupErr("");
  };

  return {
    // State
    showPartCategoryPopup,
    setShowPartCategoryPopup,
    tempPartCategoryData,
    setTempPartCategoryData,
    partCategoryPopupErr,
    isPartCategorySaving,
    isPartCategoryDeleting,
    
    // Handlers
    handleCreatePartCategory,
    handleSavePartCategory,
    handleDeletePartCategory,
    handleClosePartCategoryPopup,
  };
};
